import {defineStore} from "pinia";
import {PossibleDuplicateService} from "@/javascript/services/possibleDuplicate.service";

export const usePossibleDuplicateStore = defineStore('possibleDuplicate', {
  state: () => ({
    service: null,
    organizationId: null,

    // Grouped list of primaries with pending duplicates.
    reviews: [],
    pendingCount: 0,
    loadingReviews: false,
    meta: {
      page: 1,
      pages: 1,
      limit: 10
    },

    // The currently-viewed primary donor + its candidate matches.
    currentPrimary: null,
    currentMatches: [],
    loadingReview: false,

    successMessage: "",
  }),

  actions: {
    setOrganizationId(id) {
      if (!id) {
        throw `Organization ID is not set: ${id}`;
      }
      this.organizationId = id;
      this.service = new PossibleDuplicateService(id);
    },

    // Load the grouped list for the Duplicate Donors tab.
    async loadReviews() {
      this.loadingReviews = true;
      try {
        const res = await this.service.reviews(this.meta.page);
        this.reviews = res?.data ?? [];
        this.meta = res?.meta ?? this.meta;
        this.pendingCount = res?.meta?.pending_count ?? 0;

        // If the current page emptied out but earlier pages still have results, step back a page
        // and reload so the user isn't left on a false "no duplicates" screen.
        while (this.reviews.length === 0 && this.meta.page > 1) {
          this.meta.page -= 1;
          const previous = await this.service.reviews(this.meta.page);
          this.reviews = previous?.data ?? [];
          this.meta = previous?.meta ?? this.meta;
          this.pendingCount = previous?.meta?.pending_count ?? 0;
        }
      } catch (e) {
        console.warn("Error loading duplicate donor reviews", e);
        this.reviews = [];
      } finally {
        this.loadingReviews = false;
      }
    },

    // Load one primary donor + its pending candidates for the View page.
    async loadReview(primaryDonorId) {
      if (!this.service) {
        console.warn("possibleDuplicate service not initialized; skipping loadReview");
        this.currentPrimary = null;
        this.currentMatches = [];
        return;
      }
      this.loadingReview = true;
      this.currentPrimary = null;
      this.currentMatches = [];
      try {
        const res = await this.service.review(primaryDonorId);
        this.currentPrimary = res?.primary_donor || null;
        this.currentMatches = res?.matches || [];
      } catch (e) {
        console.warn("Error loading duplicate donor review", e);
      } finally {
        this.loadingReview = false;
      }
    },

    // Keep-separate a single match; on success remove it locally and refresh counts.
    async keepSeparate(flagId) {
      try {
        const res = await this.service.keepSeparate(flagId);
        if (res?.status === "kept_separate") {
          this.currentMatches = this.currentMatches.filter((m) => m.id !== flagId);
          if (this.pendingCount > 0) this.pendingCount -= 1;
          this.successMessage = "Records kept separate.";
          // Refresh the grouped list so a primary's count (or its removal) stays accurate.
          this.loadReviews();
        }
        return res;
      } catch (e) {
        console.error("Error keeping donor separate", e);
        return false;
      }
    },

    // Execute a merge for one candidate flag. survivingDonorId/duplicateDonorId
    // come from the compare screen's "which record is kept" selection.
    async merge(flagId, survivingDonorId, duplicateDonorId) {
      try {
        const res = await this.service.merge(survivingDonorId, duplicateDonorId);
        if (res?.success) {
          this.currentMatches = this.currentMatches.filter((m) => m.id !== flagId);
          if (this.pendingCount > 0) this.pendingCount -= 1;
          this.successMessage = "Donors merged successfully.";
          // Refresh the grouped list so counts stay accurate.
          this.loadReviews();
        }
        return res;
      } catch (e) {
        console.error("Error merging donors", e);
        return {success: false, error: "Something went wrong merging these donors."};
      }
    },

    nextPage() {
      this.meta.page++;
      this.loadReviews();
    },
    prevPage() {
      this.meta.page--;
      this.loadReviews();
    },
    moveToPage(page) {
      this.meta.page = page;
      this.loadReviews();
    },
  },

  getters: {
    hasPendingDuplicates(state) {
      return state.pendingCount > 0;
    },
  },
});
